<template>
    <template v-if="isFetching && !data">
        <h2 class="clinic-profile__section-title">Врачи в клинике</h2>
        <UIListSkeleton variant="veterinarian" :count="2"/>
    </template>
    <template v-else-if="veterinarians && veterinarians.length">
        <h2 class="clinic-profile__section-title">Врачи в клинике</h2>
        <div>
            <div class="clinic-doctors-list" ref="vetsList">
                <VeterinarItemList v-for="veterinarian in sortedVeterinarians" :key="veterinarian.id"
                    :veterinarian="veterinarian" type="all" show-reception-toggle clinic-page/>
                <Loader overlay :active="isFetching"></Loader>
            </div>
            <Pagination v-if="meta" :meta="meta" @page-changed="onPageChanged" />
        </div>
    </template>
</template>

<script setup lang="ts">
import type { Veterinarian } from "~/types/veterinar";
import type { Meta } from "~/types/request/meta";
import { scrollPageToView } from "~/utils/helpers";
import {clinicPageDoctorShowsSlots, compareClinicPageDoctors, pickNearestDoctorSlot} from "~/utils/clinicDoctorSlot";

const props = defineProps<{
    clinicId: number;
}>();

const emit = defineEmits<{
    nearestSlot: [slot: import('~/types/veterinar').VeterinarianBookingNearestSlot | null];
}>();

const page = ref<Number>(1);
const vetsList = ref<HTMLElement | null>(null);

const {apiQuery} = useApi();
const { data, pending: isFetching, refresh, error } = await useAsyncData(`clinic-${props.clinicId}-veterinarians-${page.value}`, () => apiQuery<{
    data: Veterinarian[];
    meta: Meta;
}>('public/doctors', 'GET', {
    query: { 
        clinic: props.clinicId,
        page: page.value, 
        perPage: 4
    },
}));

const veterinarians = computed(() => data.value?.data || []);
const sortedVeterinarians = computed(() =>
    [...veterinarians.value].sort(compareClinicPageDoctors),
);
const meta = computed(() => data.value?.meta as Meta || { total: 0 });

watch(sortedVeterinarians, (list) => {
    const bookable = list.find((doctor) => clinicPageDoctorShowsSlots(doctor));
    emit('nearestSlot', bookable ? pickNearestDoctorSlot(bookable) : null);
}, {immediate: true});

function onPageChanged(newPage: number) {
    page.value = newPage;
    refresh().then(() => {
        if (vetsList.value) {
            scrollPageToView(vetsList.value);
        }
    });
}
</script>

<style lang="scss">
.clinic-doctors-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
    position: relative;
    width: 100%;
    background: $color-dark-white;
    border-radius: 24px;
    padding: 12px;
    margin: 0 -8px;

    &__empty {
        text-align: center;
        margin-top: 30px;
    }

}
</style>
